import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { store } from '@/app/store';
import { env } from '@/config/env';
import type { ApiResponse } from '@/entities/api/types';
import type {
  ClinicLogoProcessing,
  ClinicLogoUploadUrlResponse,
  ClinicProfile,
  UpdateClinicProfileInput
} from '@/entities/clinic/types';
import { getPrimaryUserRole, getUserRoles } from '@/entities/user/lib/userAccess';
import type { User } from '@/entities/user/types/user';
import { apiClient } from '@/lib/api-client';

const ownerQueryKeys = {
  clinicProfile: ['clinic-profile'] as const,
  users: ['owner-users'] as const
};

export type CreateUserInput = {
  email: string;
  displayName: string;
  roles: string[];
  password: string;
  mustChangePassword?: boolean;
};

export type UpdateUserRolesInput = {
  userId: string;
  roles: string[];
};

export type UpdateUserStatusInput = {
  userId: string;
  isActive: boolean;
};

export type ResetUserPasswordInput = {
  userId: string;
  newPassword: string;
  mustChangePassword?: boolean;
};

async function getClinicProfile() {
  const response = await apiClient.get<ApiResponse<ClinicProfile> | null>('/clinic/profile', {
    validateStatus: status => (status >= 200 && status < 300) || status === 404
  });

  if (response.status === 404) {
    const state = store.getState();
    return {
      clinicId: state.auth.clinicId ?? 'clinic-main',
      clinicName: 'Clinic workspace',
      address: '',
      websiteUrl: '',
      logoUrl: null,
      brandProductName: env.VITE_APP_NAME,
      brandPrimaryColor: '#2175e4',
      supportEmail: 'support@aarogyaflow.com',
      emailDisplayName: env.VITE_APP_NAME,
      phone: '',
      gstin: '',
      updatedAtUtc: new Date().toISOString()
    } satisfies ClinicProfile;
  }

  return response.data?.data as ClinicProfile;
}

async function updateClinicProfile(payload: UpdateClinicProfileInput) {
  const { data } = await apiClient.put<ApiResponse<ClinicProfile>>('/clinic/profile', payload);
  return data.data;
}

async function createClinicLogoUpload(file: File) {
  const { data } = await apiClient.post<ApiResponse<ClinicLogoUploadUrlResponse>>('/clinic/profile/logo:upload-url', {
    fileName: file.name,
    contentType: file.type || 'application/octet-stream'
  });

  return data.data;
}

async function uploadLogoToBlob(uploadUrl: string, file: File) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream'
    },
    body: file
  });

  if (!response.ok) {
    throw new Error('Logo upload failed.');
  }
}

async function completeClinicLogoUpload(upload: ClinicLogoUploadUrlResponse) {
  const { data } = await apiClient.post<ApiResponse<ClinicLogoProcessing>>(
    '/clinic/profile/logo:complete',
    { assetId: upload.assetId, blobPath: upload.blobPath }
  );
  return data.data;
}

async function getClinicLogoStatus(assetId: string) {
  const { data } = await apiClient.get<ApiResponse<ClinicLogoProcessing>>(
    `/clinic/profile/logo/${encodeURIComponent(assetId)}/status`
  );
  return data.data;
}

async function listUsers() {
  const { data } = await apiClient.get<ApiResponse<User[]>>('/users');
  return data.data.map(normalizeUser);
}

async function createUser(payload: CreateUserInput) {
  const roles = getUserRoles(payload);
  const { data } = await apiClient.post<ApiResponse<User>>('/users', {
    ...payload,
    role: getPrimaryUserRole({ roles }),
    roles,
    mustChangePassword: payload.mustChangePassword ?? true
  });
  return normalizeUser(data.data);
}

async function updateUserRoles(payload: UpdateUserRolesInput) {
  const roles = getUserRoles(payload);
  const { data } = await apiClient.patch<ApiResponse<User | null>>(`/users/${payload.userId}/roles`, {
    role: getPrimaryUserRole({ roles }),
    roles
  });
  return data.data ? normalizeUser(data.data) : null;
}

async function updateUserStatus(payload: UpdateUserStatusInput) {
  const { data } = await apiClient.patch<ApiResponse<User | null>>(`/users/${payload.userId}/status`, {
    isActive: payload.isActive
  });
  return data.data ? normalizeUser(data.data) : null;
}

async function resetUserPassword(payload: ResetUserPasswordInput) {
  const { data } = await apiClient.post<ApiResponse<{ userId: string; updatedAtUtc: string } | null>>(
    `/users/${payload.userId}/reset-password`,
    {
      newPassword: payload.newPassword,
      mustChangePassword: payload.mustChangePassword ?? true
    }
  );
  return data.data;
}

function normalizeUser(user: User) {
  const roles = getUserRoles(user);

  return {
    ...user,
    role: getPrimaryUserRole({ roles }),
    roles,
    linkedDoctorId: user.linkedDoctorId ?? null,
    linkedDoctorName: user.linkedDoctorName ?? null
  } satisfies User;
}

export function useClinicProfile(enabled = true) {
  return useQuery({
    queryKey: ownerQueryKeys.clinicProfile,
    queryFn: getClinicProfile,
    enabled,
    staleTime: 60_000,
    retry: false
  });
}

export function useUpdateClinicProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateClinicProfile,
    onSuccess: profile => {
      queryClient.setQueryData(ownerQueryKeys.clinicProfile, profile);
    }
  });
}

export function useUploadClinicLogo() {
  return useMutation({
    mutationFn: async (file: File) => {
      const upload = await createClinicLogoUpload(file);
      await uploadLogoToBlob(upload.uploadUrl, file);
      return completeClinicLogoUpload(upload);
    }
  });
}

export function useClinicLogoStatus(assetId: string | null) {
  return useQuery({
    queryKey: ['clinic-profile', 'logo-status', assetId],
    queryFn: () => getClinicLogoStatus(assetId!),
    enabled: Boolean(assetId),
    refetchInterval: query => (query.state.data?.status === 'Processing' ? 1_000 : false),
    retry: false
  });
}

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: ownerQueryKeys.users,
    queryFn: listUsers,
    enabled
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ownerQueryKeys.users });
    }
  });
}

export function useUpdateUserRoles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserRoles,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ownerQueryKeys.users });
    }
  });
}

export function useUpdateUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserStatus,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ownerQueryKeys.users });
    }
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: resetUserPassword
  });
}
